<template>
  <div>
    <h4 class="mb-3 pb-3 border-bottom d-flex">
      <span>
        {{ $t('agents.commissionsList') }}
      </span>

      <d-button
        v-if="hasSelectedPayOuts"
        theme="success"
        @click.prevent="payOut"
        class="ml-auto">
        <div
          v-if="payoutInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        {{ $t('agents.commissions.payOut') }}
      </d-button>
    </h4>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="agent-commissions-filters-form"
        tag="form"
        style="width: 100%;">
        <div class="d-flex">
          <d-input
            v-model="filters.order_name"
            :placeholder="$t('ui.orderName')"
            class="w-25" />

          <date-range-picker
            ref="picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="ml-2 w-25"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="orderDateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>

          <v-select
            class="ml-2 w-25"
            :options="orderStatusOptions"
            v-model="filters.order_status"
            :placeholder="$t('status.statusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            class="w-25 ml-2"
            :options="[{
              text: $t('agents.commissions.comissionStatus.PAID'),
              value: 'PAID'
            }, {
              text: $t('agents.commissions.comissionStatus.TO_PAY'),
              value: 'TO_PAY'
            }]"
            v-model="filters.status"
            :placeholder="$t('agents.commissions.commissionStatusOption')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            :options="currencyOptions"
            v-model="filters.currency_id"
            :searchable="false"
            :clearable="true"
            :reduce="currency => currency.value"
            :placeholder="$t('settings.currency.chooseCurrency')"
            label="text"
            class="w-25 ml-2">
            <template slot="no-options">
              {{ $t('settings.currency.noCurrenciesAvailable') }}&hellip;
            </template>
          </v-select>
        </div>

        <div class="d-flex mt-2">
          <d-input
            v-model="filters.settlement_number"
            :placeholder="$t('agents.commissions.settlementNumber')"
            class="w-25" />

          <date-range-picker
            ref="picker2"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="ml-2 w-50"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="invoiceDateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStartAlt(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>

          <v-select
            class="w-25 ml-2"
            :options="[{
              text: $t('agents.commissions.settlementStatus.PAID'),
              value: 'PAID'
            }, {
              text: $t('agents.commissions.settlementStatus.UNPAID'),
              value: 'UNPAID'
            }]"
            v-model="filters.settlement_status"
            :placeholder="$t('agents.commissions.settlementStatusOption')"
            :searchable="false"
            label="text">
          </v-select>

          <d-button
            theme="info"
            outline
            class="text-nowrap ml-2"
            @click.prevent.stop="validateFiltersForm">{{ $t('ui.search') }}</d-button>

          <d-button
            theme="danger"
            outline
            class="text-nowrap ml-2"
            @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
        </div>
      </validation-observer>
    </d-row>

    <d-container fluid class="main-content-container px-0">
      <d-card style="width: 100%;">
        <table class="table mt-0 mb-0 full-width">
          <thead class="bg-light">
            <tr>
              <th scope="col" class="border-0 pr-0">
                <d-checkbox
                  class="mb-0 pr-0"
                  v-model="selectAll"
                  @change="toggleItems" />
              </th>
              <th scope="col" class="border-0">{{ $t('agents.commissions.orderName') }}</th>
              <th scope="col" class="border-0">{{ $t('agents.commissions.orderStatus') }}</th>
              <th scope="col" class="border-0">{{ $t('agents.commissions.orderDate') }}</th>
              <th scope="col" class="border-0">{{ $t('agents.commissions.invoiceExists') }}</th>
              <th scope="col" class="border-0">{{ $t('agents.commissions.paymentStatus') }}</th>
              <th scope="col" class="border-0">{{ $t('agents.commissions.commission') }}</th>
            </tr>
          </thead>
          <tbody v-if="allDataLoaded">
            <tr
              v-for="(commission, index) of commissions"
              :key="'commission-' + index">
              <td
                class="align-middle pr-0">
                <d-checkbox
                  v-if="commission.can_be_settled"
                  class="mb-0 pr-0"
                  v-model="selectedItems[commission.id]" />
              </td>
              <td class="align-middle">
                <a
                  href="#"
                  @click.prevent="openOrderInNewTab(commission.order.id)">
                  {{ commission.order.order_name }}
                </a>
              </td>
              <td class="align-middle">
                <status-badge
                  :status="commission.order.status" />
              </td>
              <td
                class="align-middle"
                v-pure-html="commission.order.created_at.split(' ').join('<br>')">
              </td>
              <td class="align-middle">
                <span
                  v-if="commission.settlement_id"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-else
                  class="text-daner">
                  {{ $t('ui.no') }}
                </span>

                <small
                  v-if="commission.settlement_id"
                  class="d-block">
                  ({{ commission.settlement.amount_net }} {{ commission.settlement.currency }})
                </small>
              </td>
              <td class="align-middle">
                <template v-if="commission.settlement_id">
                  <d-badge
                    v-if="commission.settlement.status === 'PAID'"
                    theme="success">
                    {{ $t('agents.commissions.settlementStatus.PAID') }}
                  </d-badge>
                  <d-badge
                    v-if="commission.settlement.status === 'UNPAID'"
                    theme="danger">
                    {{ $t('agents.commissions.settlementStatus.UNPAID') }}
                  </d-badge>
                </template>
              </td>
              <td class="align-middle">
                <span
                  v-if="commission.settlement.status === 'PAID'"
                  class="text-success">
                  {{ commission.commission_amount }} {{ commission.currency_code }}
                </span>
                <span
                  v-if="commission.settlement.status === 'UNPAID'"
                  class="text-danger">
                  {{ commission.commission_amount }} {{ commission.currency_code }}
                </span>
                <br>
                <small>
                  {{ $t('agents.commissions.comissionStatus.' + commission.agent_settlement_status) }}
                </small>
              </td>
            </tr>
            <tr v-if="commissions.length === 0">
              <td
                colspan="7"
                class="text-center">
                {{ $t('agents.commissions.noCommissionsToDisplay') }}&hellip;
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>

      <pagination
        :meta-data="paginationMetaData"
        :initial-page="currentPage" />
    </d-container>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import HasFilters from './../../mixins/HasFilters.vue';
import HasPagination from './../../mixins/HasPagination.vue';
import ListUtils from './../../utils/ListUtils.js';
import Pagination from './../../components/common/Pagination.vue';
import StatusBadge from './../common/StatusBadge.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Vue from 'vue';

export default {
  name: 'agent-commissions',
  mixins: [
    HasFilters,
    HasPagination
  ],
  props: [
    'agentID'
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'status-badge': StatusBadge
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies;
    },
    currencyOptions () {
      return this.currencies.map(currency => ({
        value: currency.id,
        text: currency.code
      }));
    },
    disableFiltersInSubview () {
      return true;
    },
    hasSelectedPayOuts () {
      let commissionsToPayOut = Object.keys(this.selectedItems);
      let hasAny = false;

      for (let i = 0; i < commissionsToPayOut.length; i++) {
        if (this.selectedItems[commissionsToPayOut[i]]) {
          hasAny = true;
        }
      }

      return hasAny;
    },
    selectedToPayOut () {
      let commissionsToPayOut = Object.keys(this.selectedItems);
      let toPayOut = [];

      for (let i = 0; i < commissionsToPayOut.length; i++) {
        if (this.selectedItems[commissionsToPayOut[i]]) {
          toPayOut.push(parseInt(commissionsToPayOut[i], 10));
        }
      }

      return toPayOut;
    },
    orderStatusOptions () {
      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    selectedCommissions () {
      return Object.keys(this.selectedItems).filter(item => !!this.selectedItems[item]).map(item => parseInt(item, 10)).sort();
    },
    visibleCommissions () {
      return this.commissions.filter(commission => commission.can_be_settled).map(commission => commission.id).sort();
    }
  },
  data() {
    return {
      commissions: [],
      currencies: [],
      orderDateRange: {
        startDate: null,
        endDate: null
      },
      invoiceDateRange: {
        startDate: null,
        endDate: null
      },
      filters: {
        status: null,
        currency_id: null,
        order_name: '',
        order_status: null,
        settlement_number: '',
        settlement_status: null,
        settlement_invoice_date_from: null,
        settlement_invoice_date_to: null
      },
      loaded: false,
      loadError: false,
      loadedCurrencies: false,
      removeInProgress: false,
      selectedItems: {},
      payoutInProgress: false,
      selectAll: false
    };
  },
  watch: {
    'selectedCommissions': {
      handler () {
        if (this.visibleCommissions.length && this.selectedCommissions.join('-') === this.visibleCommissions.join('-')) {
          this.selectAll = true;
        } else if (this.selectedCommissions.length) {
          this.selectAll = true;
        } else {
          this.selectAll = false;
        }
      }
    }
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
    this.$bus.$on('reload-agent-commissions-list', this.debouncedLoadFilteredData);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      Vue.set(this, 'selectedItems', {});

      let whereData = {
        agent_id: this.agentID
      };

      if (
        this.filters.order_name ||
        this.filters.order_status !== null ||
        this.filters.order_created_at_from !== null ||
        this.filters.order_created_at_to !== null
      ) {
        whereData.order = {}

        if (this.filters.order_name) {
          whereData.order.name = this.filters.order_name;
        }

        if (this.orderDateRange.startDate) {
          whereData.order.created_at_from = this.prepareDateString(this.orderDateRange.startDate);
        }

        if (this.orderDateRange.endDate) {
          whereData.order.created_at_to = this.prepareDateString(this.orderDateRange.endDate);
        }

        if (this.filters.order_status !== null) {
          whereData.order.status = this.filters.order_status.value;
        }
      }

      if (
        this.filters.settlement_number ||
        this.filters.settlement_status !== null ||
        this.invoiceDateRange.startDate !== null ||
        this.invoiceDateRange.endDate !== null
      ) {
        whereData.settlement = {};

        if (this.filters.settlement_number) {
          whereData.settlement.number = this.filters.settlement_number;
        }

        if (this.invoiceDateRange.startDate) {
          whereData.settlement.invoice_date_from = this.prepareDateString(this.invoiceDateRange.startDate);
        }

        if (this.invoiceDateRange.endDate) {
          whereData.settlement.invoice_date_to = this.prepareDateString(this.invoiceDateRange.endDate);
        }

        if (this.filters.settlement_status !== null) {
          whereData.settlement.status = this.filters.settlement_status.value;
        }
      }

      if (this.filters.status !== null) {
        whereData.status = this.filters.status.value;
      }

      if (this.filters.currency_id !== null) {
        whereData.currency_id = this.filters.currency_id;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/agents/commissions/items?page=' + this.currentPage,
        listField: 'commissions',
        params: {
          where: whereData
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    payOut () {
      this.payoutInProgress = true;

      FormUtils.submitInBackground({
        method: 'post',
        $http: this.$http,
        endpoint: '/agents/commissions/store',
        formData: {
          items: this.selectedToPayOut
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('agents.commissions.errorWhenPayOut'),
        successAction: () => {
          this.payoutInProgress = false;
          this.loadFilteredData();
          this.$bus.$emit('reload-agent-settlements-list');
        },
        failAction: () => {
          this.payoutInProgress = false;
        }
      });
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    openOrderInNewTab (orderID) {
      window.open(this.$router.resolve('/zamowienia/edycja/' + orderID).href, '_blank');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['agent-commissions-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters.status = null;
      this.filters.currency_id = null;
      this.filters.order_name = '';
      this.filters.order_status = null;
      this.filters.settlement_number = '';
      this.filters.settlement_status = null;

      this.orderDateRange = {
        startDate: null,
        endDate: null
      };

      this.invoiceDateRange = {
        startDate: null,
        endDate: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('agents.commissions.orderDate') + ' ' + this.$t('ui.from');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateStartAlt (input) {
      if (input === null) {
        return this.$t('ui.invoiceDate') + ' ' + this.$t('ui.from');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    toggleItems () {
      Vue.nextTick(() => {
        if (this.selectAll) {
          for (let i = 0; i < this.visibleCommissions.length; i++) {
            let id = this.visibleCommissions[i];
            Vue.set(this.selectedItems, id, true);
          }
        } else {
          Vue.set(this, 'selectedItems', []);
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('reload-agent-commissions-list', this.debouncedLoadFilteredData);
  }
};
</script>

<style lang="scss" scoped>

</style>
